import React, { useMemo } from 'react';

import { Tooltip } from 'antd';
import { NavLink, useLocation } from 'react-router-dom';
import styled from 'styled-components';

import useAppState from '../hooks/useAppState';
import useBoxesStore from '@/features/boxes/hooks/useBoxesStore';
import useContractsStore from '@/features/buy-n-rent/hooks/useContractsStore';
import { useLPUsersStore } from '@/features/lp-account-users';
import useShipmentsStore from '@/features/shipments/hooks/useShipmentsStore';
import { storeSelector } from '@/helpers/paginatedStoreHelpers';
import { useAuth0 } from '@/shared/auth0';

const Text = styled.p`
  margin: 0;
  margin-top: 0.5rem;
  font-family: TTNormsPro-Bold;
  text-transform: uppercase;
  font-size: 13px;
`;

const StyledNavLink = styled(NavLink)<{ disabled?: boolean }>`
  padding: 1.5rem 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  color: ${({ theme, disabled }) =>
    disabled
      ? theme.colors.adminPanel.darkgrey
      : theme.colors.adminPanel.black};

  &.active-nav-link {
    &.active {
      background: white;
      position: relative;
      color: #0c6b64;
      box-shadow: 0px 0px 12px #0c6b6433;
    }
  }
`;

interface NavElementProps {
  icon: any;
  to: string;
  text: string;
  requiredScopes?: string[];
}

const NavElement = ({
  icon: Icon,
  requiredScopes,
  to,
  text,
}: NavElementProps) => {
  const { isAuthenticated } = useAuth0();
  const location = useLocation();
  const userScopes = useAppState((state) => state.scopes);
  const shipmentStoreDispatch = useShipmentsStore((state) => state.dispatch);
  const productStoreDispatch = useBoxesStore((state) => state.dispatch);
  const lpAccountStoreDispatch = useLPUsersStore((state) => state.dispatch);
  const contractsDispatch = useContractsStore(storeSelector).dispatch;

  //run all dispatches reset before clicking on the nav, but only if navigating to a different route
  const handleReset = () => {
    // Don't reset if clicking on the already active menu item
    if (
      location.pathname === to ||
      (to !== '/' && location.pathname.startsWith(`${to}/`))
    ) {
      return;
    }

    shipmentStoreDispatch({ type: 'reset' });
    productStoreDispatch({ type: 'reset' });
    lpAccountStoreDispatch({ type: 'reset' });
    contractsDispatch({ type: 'reset' });
  };

  const disabled = useMemo(() => {
    if (isAuthenticated) {
      return !(
        !requiredScopes ||
        requiredScopes.length === 0 ||
        requiredScopes.some((val) => userScopes.includes(val))
      );
    }

    return true;
  }, [isAuthenticated, requiredScopes, userScopes]);

  return disabled ? (
    <Tooltip placement="right" title="Sorry, you are not allowed to view this!">
      <StyledNavLink to={to} disabled={disabled}>
        <Icon fill="currentColor" />
        <Text>{text}</Text>
      </StyledNavLink>
    </Tooltip>
  ) : (
    <StyledNavLink to={to} className="active-nav-link" onClick={handleReset}>
      <Icon />
      <Text>{text}</Text>
    </StyledNavLink>
  );
};

export default NavElement;
